import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { ChecklistData, ChecklistItem, ChecklistTemplate } from "../../db/schema";
import Skeleton from "react-loading-skeleton";

interface ChecklistsProps {
  checklist: {
    template: ChecklistTemplate;
    data: ChecklistData;
    completed_at?: string;
    created_at?: string;
    updated_at?: string;
  };
  onChange: (updated: ChecklistData) => void;
  isReadOnly?: boolean;
}

function Checklists({ checklist, onChange, isReadOnly = false }: ChecklistsProps) {
  const [localData, setLocalData] = useState<ChecklistData>(checklist.data);

  // Sync local data when the data prop changes
  useEffect(() => {
    setLocalData(checklist.data);
  }, [checklist.data]);

  // Group items by category
  const groupedItems = checklist.template.items.reduce((groups, item) => {
    const category = item.category || "General";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(item);
    return groups;
  }, {} as Record<string, typeof checklist.template.items>);

  // Sort items within each category by order
  Object.keys(groupedItems).forEach((category) => {
    groupedItems[category].sort((a, b) => a.order - b.order);
  });

  const handleItemChange = (itemId: string, checked: boolean, additionalNotes?: string) => {
    if (isReadOnly) return;

    // Find the item in the template to check if it's required
    const templateItem = checklist.template.items.find((item) => item.id === itemId);

    // Prevent unchecking required items
    if (templateItem?.required && !checked && localData.items[itemId]?.checked) {
      return;
    }

    const currentItemData = localData.items[itemId] || {};

    // Only update checked_at if the checked status is actually changing
    const isCheckStatusChanging = currentItemData.checked !== checked;

    const newData = {
      ...localData,
      items: {
        ...localData.items,
        [itemId]: {
          checked,
          // Keep existing checked_at if only notes are changing, otherwise update it
          checked_at: isCheckStatusChanging
            ? (checked ? new Date().toISOString() : undefined)
            : currentItemData.checked_at,
          additionalNotes: additionalNotes !== undefined ? additionalNotes : currentItemData.additionalNotes,
        },
      },
    };

    setLocalData(newData);
    onChange(newData);
  };

  const getTotalItems = () => checklist.template.items.length;
  const getCompletedItems = () =>
    Object.values(localData.items).filter((item) => item.checked).length;

  const getProgress = () => {
    const total = getTotalItems();
    const completed = getCompletedItems();
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const getCategoryProgress = (items: ChecklistItem[]) => {
    const total = items.length;
    const completed = items.filter(
      (item) => localData.items[item.id]?.checked
    ).length;
    return { completed, total };
  };

  const ChecklistSkeleton = () => (
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-2 bg-base-200 rounded-lg">
          <Skeleton height={16} width="100%" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="pt-2 md:pt-0">
      <div className="flex items-center prose mb-2">
        <CheckCircleIcon className="size-5 text-blue-500 dark:text-blue-300" />
        <h3 className="m-0 ml-2 text-base">Checklist</h3>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-600 dark:text-base-content/70 mb-1">
          <span>{getCompletedItems()} of {getTotalItems()} completed</span>
          <span className="font-semibold">{getProgress()}%</span>
        </div>
        <div className="bg-base-300 rounded-full h-1.5">
          <div
            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          ></div>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-3 mb-24">
        {Object.entries(groupedItems).map(([category, items]) => {
          const { completed, total } = getCategoryProgress(items);
          return (
            <div key={category}>
              {Object.keys(groupedItems).length > 1 && (
                <div className="flex items-center justify-between mb-2 pb-1 border-b border-base-300">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-base-content/80 uppercase tracking-wide">
                    {category}
                  </h4>
                  <span className="text-xs text-blue-600 dark:text-blue-300 font-medium">
                    {completed}/{total}
                  </span>
                </div>
              )}

              <div className="space-y-2">
                {items.map((item) => {
                  const isChecked = localData.items[item.id]?.checked || false;
                  const isRequiredAndChecked = item.required && isChecked;

                  return (
                  <div key={item.id} className="space-y-1">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={`sidebar-${item.id}`}
                        checked={isChecked}
                        onChange={(e) =>
                          handleItemChange(item.id, e.target.checked)
                        }
                        disabled={isReadOnly || isRequiredAndChecked}
                        className="mt-0.5 h-3.5 w-3.5 text-blue-600 dark:text-blue-300 rounded border-gray-300 dark:border-base-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`sidebar-${item.id}`}
                        className={`flex-1 ${isRequiredAndChecked ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-xs ${
                              isChecked
                                ? "line-through text-base-content/60"
                                : "text-base-content"
                            }`}
                          >
                            {item.text}
                          </span>
                          {item.required && (
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" title="Required"></span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-[11px] text-base-content/60 mt-0.5">
                            {item.description}
                          </p>
                        )}
                      </label>
                    </div>
                    {/* Additional Notes Textarea */}
                    {isChecked && item.additionalNotesEnabled && (
                      <div className="ml-5 mt-1">
                        <textarea
                          value={localData.items[item.id]?.additionalNotes || ""}
                          onChange={(e) =>
                            handleItemChange(item.id, true, e.target.value)
                          }
                          disabled={isReadOnly}
                          placeholder="Additional notes..."
                          rows={2}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-base-200 disabled:cursor-not-allowed"
                        />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Checklists;
